/**
 * The legacy `numberKeypress` character filter for `input[data-type="number"]`
 * / `[data-formatter_type=decimal|integer]` — digits, one leading `-` when
 * `data-min` allows it, one `.` on decimal fields, nothing else.
 *
 * Legacy ships the same function in three places (`main.js`, `cms.main.js` and
 * `component_selector/view/component_selector.js:447`); this is the one copy.
 * `numberInputBackstop` below is the half legacy never had — a keypress
 * handler cannot see a paste, a drop, an autofill or an IME commit.
 *
 * ★ TWO DELIBERATE DEVIATIONS from legacy. The first is the whole point of
 * the filter; the second is the `-` branch below, which consults `data-min`
 * where legacy consults it only when the whole field is selected.
 * Legacy tests `e.keyCode` inside a `keypress` handler:
 *
 *   if (e.keyCode >= 48 && e.keyCode <= 57) return;   // "digits top row"
 *   if (e.keyCode >= 96 && e.keyCode <= 105) return;  // "digits numpad"
 *
 * Those ranges are `keydown` codes. On `keypress`, `keyCode` is the CHARACTER
 * code — so the second line reads as 96–105 = `` ` ``, `a`, `b`, `c`, `d`, `e`,
 * `f`, `g`, `h`, `i`, and lowercase a–i sail straight through into a number
 * box. (`[8, 9, 37, 38, 39, 40, 46]` "backspace/tab/arrows" is the same
 * mistake: on keypress those are `%`, `&`, `'`, `(` and `.`.) Switching to
 * `e.key` implements what the ranges were meant to say. The pre-existing React
 * ports in `project-board/hooks/useGlobalInputBehaviors.ts` and
 * `cms-admin/useCmsFormControls.ts` made the same call.
 */
export function numberKeypressFilter(e: KeyboardEvent, input: HTMLInputElement): void {
  const key = e.key;
  const value = input.value;
  const cursorPos = input.selectionStart ?? 0;
  const format = numberFieldFormat(input);
  const min = numberFieldMin(input);
  const isAllSelected = input.selectionStart === 0 && input.selectionEnd === value.length;

  if (format !== 'decimal' && key === '.') {
    e.preventDefault();
    return;
  }
  // Select-all: the existing text is about to be replaced, so `.`/`-` are
  // judged against the field's own rules rather than against what is in the box.
  if (isAllSelected) {
    if (key === '.') {
      if (format !== 'decimal') e.preventDefault();
      return;
    }
    if (key === '-') {
      if (!(min < 0)) e.preventDefault();
      return;
    }
  }

  if (/^[0-9]$/.test(key)) return;
  if (key === '-') {
    // ★ `min < 0` here is a DEVIATION from legacy, and a deliberate one.
    // Legacy tests `data-min` only in the select-all branch above; this branch
    // checks position alone, so in a `data-min="0"` box that already holds
    // digits a leading `-` could be typed and only the blur validator clamped
    // it away. That left the typing filter and `numberInputBackstop`
    // disagreeing about one character — the backstop reads `data-min` on every
    // path, so the minus appeared and then vanished as the next event landed.
    // Making the two agree is worth one character of legacy infidelity: the
    // field could never hold the value anyway.
    if (cursorPos !== 0 || value.includes('-') || !(min < 0)) e.preventDefault();
    return;
  }
  if (key === '.') {
    if (format !== 'decimal' || value.includes('.')) e.preventDefault();
    return;
  }
  // Control keys ('Enter', 'Backspace', 'Tab', …) — most raise no keypress at
  // all, but never block one that does.
  if (key.length > 1) return;
  e.preventDefault();
}


export type NumberFieldFormat = 'decimal' | 'integer';

/**
 * Integer vs decimal, resolved exactly as the keypress filter above has always
 * resolved it: `data-formatter_type="decimal"`, or `data-step="0.01"` (legacy's
 * own marker for a 2-dp box — `validateNumberField` branches on the same test).
 * A field with neither is an integer field.
 *
 * Shared, so the backstop below can never disagree with the typing filter:
 * whichever way a given box treats `.`, it treats it the same for both.
 */
function numberFieldFormat(input: HTMLInputElement): NumberFieldFormat {
  const step = Number(input.dataset.step || 1);
  return input.dataset.formatter_type === 'decimal' || step === 0.01 ? 'decimal' : 'integer';
}

/** `data-min`, or -Infinity when the field declares none. A non-numeric
 * `data-min` yields NaN, which fails `min < 0` and so forbids `-`. Legacy
 * applied that only to a select-all replacement; since the minus-parity fix
 * the keypress filter applies it to every leading `-`, which is what lets the
 * backstop and the typing filter agree. */
function numberFieldMin(input: HTMLInputElement): number {
  return input.dataset.min !== undefined ? Number(input.dataset.min) : -Infinity;
}

/**
 * Fold the full-width forms of the characters a number box accepts onto their
 * ASCII equivalents: `１２` is twelve, typed on a Japanese or Chinese keyboard
 * in full-width mode, and deleting those digits as "not 0-9" would be wrong
 * where converting them is obviously right.
 *
 * ★ Deliberately NOT `String.prototype.normalize('NFKC')`, which would be the
 * one-liner. NFKC folds far more than width: `½` becomes `1⁄2`, and once the
 * fraction slash is stripped as punctuation that box reads `12`. A hand-built
 * 1:1 map cannot invent a digit that way, and being 1:1 it also keeps the
 * caret arithmetic below exact — NFKC can change a string's length.
 */
const FULL_WIDTH: Record<string, string> = {
  '\uFF10': '0', '\uFF11': '1', '\uFF12': '2', '\uFF13': '3', '\uFF14': '4',
  '\uFF15': '5', '\uFF16': '6', '\uFF17': '7', '\uFF18': '8', '\uFF19': '9',
  // Point. A Japanese IME in kana mode does NOT send U+FF0E for the `.` key:
  // the default kuten setting sends the ideographic full stop, and half-width
  // katakana mode its half-width form. Mapping the full-width one alone meant
  // `１２。５` lost its point and glued into `125` — the exact failure the
  // "ON AN INTEGER FIELD A POINT TRUNCATES" rule below exists to prevent,
  // arriving through a different door.
  '\uFF0E': '.', // full-width full stop
  '\u3002': '.', // ideographic full stop  。
  '\uFF61': '.', // half-width ideographic full stop  ｡
  // Comma.
  '\uFF0C': ',', // full-width comma
  '\u3001': ',', // ideographic comma  、
  '\uFF64': ',', // half-width ideographic comma  ､
  // Minus. The romaji table maps the `-` key to the prolonged-sound mark, so
  // that is what a leading minus looks like when typed in kana mode.
  '\uFF0D': '-', // full-width hyphen-minus
  '\u2212': '-', // minus sign proper, what some keyboards and Excel emit
  '\u30FC': '-', // katakana-hiragana prolonged sound mark  ー
  '\uFF70': '-', // half-width prolonged sound mark  ｰ
};

/**
 * ★ THE BACKSTOP, and the reason a keypress filter alone is not enough.
 *
 * `keypress` only fires for a keystroke the browser turns into a character
 * directly. It does NOT fire for a paste, a drag-and-drop, an autofill, or —
 * the one that matters most here — **text committed by an IME**. On an
 * Armenian or Chinese keyboard layout the composed text arrives as an `input`
 * event with no `keypress` at all, so `ննն` went straight into X-Offset on a
 * host whose typing filter was working perfectly. Measured in Chrome on the
 * box before this was added.
 *
 * So every route is caught at the one event all of them raise. The value is
 * repaired in place rather than refused: the legal characters survive and the
 * caret keeps its place among them, so typing `1a2` leaves `12` with the caret
 * still after the `2`.
 *
 * WHAT IT KEEPS, and why each one:
 *  · digits, always — except after the point on an integer field, see below;
 *  · `.` on a decimal field, the first one only — the keypress rule;
 *  · a leading `-`, where `allowNegative` says so — the keypress rule;
 *  · `,` — NOT typeable (the keypress filter blocks it) but legitimately
 *    PRESENT: the blur formatters write `1,200.00` into these boxes, and
 *    stripping it here would make the separator vanish the moment the user
 *    typed a digit into a formatted box. Every commit path already drops it
 *    (`BareInput` commits `raw.replace(/,/g, '')`).
 *
 * ★ ON AN INTEGER FIELD A POINT TRUNCATES — it does not simply vanish.
 * Deleting the `.` and reading on is the obvious implementation and it is
 * badly wrong: `12,000.50` pasted into an airflow box would become `12,00050`
 * and commit as `1200050`, a hundred times the intended number, silently.
 * Everything from the first `.` is dropped instead, so `12,000.50` → `12,000`
 * and `2.5` → `2`. That still only ever REMOVES characters — it never rounds,
 * and it never invents a digit the user did not type.
 *
 * Half-typed values are deliberately left alone — `''`, `'-'`, `'.'`, `'12.'`
 * on a decimal field are all states you pass through on the way to a real
 * number and all survive unchanged.
 */
function sanitizeNumberValue(value: string, decimal: boolean, allowNegative: boolean): string {
  let out = '';
  let dotTaken = false;
  for (const raw of value) {
    const ch = FULL_WIDTH[raw] ?? raw;
    if ((ch >= '0' && ch <= '9') || ch === ',') {
      out += ch;
    } else if (ch === '.') {
      if (!decimal) break; // integer field: the fraction is dropped, not glued on
      if (dotTaken) continue;
      out += ch;
      dotTaken = true;
    } else if (ch === '-' && allowNegative && out === '') {
      out += ch;
    }
  }
  return out;
}

/**
 * True while an IME still owns the text — do not touch the box.
 *
 * A composing IME (Japanese, Chinese, Korean) holds a marked range in the
 * input and fires `input` events for each partial composition. Rewriting the
 * value through the prototype setter during that, as the backstop does, makes
 * Chrome cancel or commit the composition early and can make Safari re-insert
 * the committed text — so a user would be unable to compose at all, which is
 * a worse bug than the one this module exists to fix.
 *
 * Hosts therefore register their handler for BOTH `input` and
 * `compositionend` and gate it on this. Both are needed, and in both orders:
 * Chrome fires its last `input` with `isComposing === true` BEFORE
 * `compositionend`, so the skip alone would let the composed text through;
 * Safari fires `compositionend` first, so that pass does the repair and the
 * trailing `input` finds the value already clean and returns.
 */
export function isLiveComposition(e: Event): boolean {
  return e.type === 'input' && (e as InputEvent).isComposing === true;
}

/**
 * Repair `input.value` in place if it holds anything the field cannot hold.
 *
 * ★ CALLING RULE: bind your handler to BOTH `input` and `compositionend`, and
 * return early from it while `isLiveComposition(e)` — calling this on every
 * `input` event rewrites the box mid-composition and makes an IME unusable.
 * See `isLiveComposition` for why both events are needed, in both orders.
 *
 * `opts` lets a host override the two rules that are read off the element, for
 * the one host whose typing filter does not use this module's: CMS Admin
 * allows `.` on every numeric box AND a leading `-` regardless of `data-min`
 * (`cms.main.js:331-368`), so it passes both and its backstop cannot disagree
 * with its own keypress filter.
 *
 * ★ `type="text"` ONLY. A native `type="number"` box needs no repair — the
 * browser already refuses letters — and repairing one is actively harmful:
 * `selectionStart` is null there and `setSelectionRange` throws
 * InvalidStateError, which would abort this function AFTER the value had been
 * rewritten and BEFORE the `input` event that tells React about it, leaving a
 * controlled box's state out of step with its DOM. It would also mangle
 * exponent notation the browser accepts (`1e5` → `15`). A handful of guarded
 * boxes are native number inputs: `CoilSelector:4100`, CMS
 * `frameSettingsPanel:558/607/621`, `LugsSettingsTab:406`.
 */
export function numberInputBackstop(
  input: HTMLInputElement,
  opts?: { format?: NumberFieldFormat; allowNegative?: boolean }
): void {
  if (input.type !== 'text') return;
  const decimal = (opts?.format ?? numberFieldFormat(input)) === 'decimal';
  const allowNegative = opts?.allowNegative ?? numberFieldMin(input) < 0;
  const value = input.value;
  const cleaned = sanitizeNumberValue(value, decimal, allowNegative);
  if (cleaned === value) return;

  // Keep the caret among the characters that survived: count how many of the
  // ones before it were kept.
  const caret = input.selectionStart ?? value.length;
  const keptBeforeCaret = sanitizeNumberValue(value.slice(0, caret), decimal, allowNegative).length;

  // Assign through the prototype setter so React's value tracker is bypassed:
  // the tracker then still holds the pre-repair string, the `input` event we
  // dispatch is seen as a change, and a CONTROLLED box (formKit's `BareInput`)
  // re-renders from the repaired text instead of putting the junk back. An
  // uncontrolled box simply keeps what we wrote. The dispatch cannot loop —
  // the next pass finds the value already clean and returns above.
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  if (setter) setter.call(input, cleaned);
  else input.value = cleaned;
  input.setSelectionRange(keptBeforeCaret, keptBeforeCaret);
  input.dispatchEvent(new Event('input', { bubbles: true }));
}
